import { useCallback, useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box, Typography } from '@lyearn/ui';
import FilterItem from '@/components/Filter/FilterItem';
import { FilterItemTooltipProps } from '@/components/Filter/types';
import { isItemSelected } from '@/components/Filter/utils';
import Loader from '@/components/Loader';
import VirtualizedGroupedList from '@/components/VirtualizedGroupedList';
import NoResultsFound from '@/modules/tasks/features/actionItems/components/NoResultsFound';

import { NewFilterOption } from '../../types';
import SelectionHeader from './components/SelectionHeader';
import { FilterVirtualListProps } from './types';

const FilterVirtualList = (props: FilterVirtualListProps) => {
  const {
    fetchMore,
    groups,
    fetching,
    optionsActions,
    listVariant,
    initialSelectedOptions,
    isSearching,
    itemRenderer,
    searchText,
    setSearchText,
  } = props;
  const {
    methods: { toggleOption, addSingle },
    selectedOptions,
  } = optionsActions;

  const { isMobile } = useScreen();
  const showLoader = fetching && !isMobile && !isSearching;
  const isSingleSelect = listVariant.includes('single');

  const { groupCounts, filterOptions } = useMemo(() => {
    const counts: number[] = [];
    const filterOptions: NewFilterOption[] = [];
    groups.forEach((group) => {
      const { options } = group;
      counts.push(options.length);
      filterOptions.push(...options);
    });

    return { groupCounts: counts, filterOptions };
  }, [groups]);

  const renderGroupContent = useCallback(
    (index: number) => {
      const filterGroup = groups[index];

      if (!filterGroup.label) {
        return <Box className="h-1"></Box>;
      }

      // in single select we have simple group labels
      return (
        <Typography
          as="p"
          className="mx-12 mb-2 flex h-38 items-center bg-bg-primary px-8"
          color="text-text-placeholder"
          variant="label-caps">
          {filterGroup.label}
        </Typography>
      );
    },
    [groups],
  );

  const renderItemContent = useCallback(
    (index: number, groupIndex: number) => {
      const option = filterOptions[index];

      // VirtualizedGroupedList returns extra index in some cases - https://github.com/petyosi/react-virtuoso/issues/319
      if (!option) {
        return <Box className="h-1" />;
      }

      const isSelected = isItemSelected(selectedOptions, option);

      const getTooltipProps: FilterItemTooltipProps = ({ option }: { option: NewFilterOption }) => {
        return {
          title: option.label,
          variant: 'default',
          placement: 'right',
        };
      };

      return (
        <Box className="px-8 xs:px-12">
          <FilterItem
            getTooltipProps={getTooltipProps}
            isSelected={isSelected}
            isSingleSelect={isSingleSelect}
            itemRenderer={itemRenderer}
            option={option}
            onClick={isSingleSelect ? addSingle : toggleOption}
          />
        </Box>
      );
    },
    [addSingle, filterOptions, isSingleSelect, itemRenderer, selectedOptions, toggleOption],
  );

  if (filterOptions && filterOptions.length === 0 && searchText) {
    return <NoResultsFound searchText={searchText} setSearchText={setSearchText} />;
  }

  return (
    <Box className={cn('filter-items flex-1 overflow-auto')}>
      <VirtualizedGroupedList
        components={{
          Footer: () => (showLoader ? <Loader showLabel={false} /> : null),
          Header: () => {
            return isSearching ? null : (
              <SelectionHeader
                filterOptions={filterOptions}
                initialSelectedOptions={initialSelectedOptions}
                itemRenderer={itemRenderer}
                listVariant={listVariant}
                methods={optionsActions.methods}
                selectedOptions={selectedOptions}
              />
            );
          },
        }}
        endReached={fetchMore}
        groupContent={renderGroupContent}
        groupCounts={groupCounts}
        itemContent={renderItemContent}
      />
    </Box>
  );
};

export default FilterVirtualList;
